import { createSlice } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";

// QueryBuilder client-state slice.
//
// Only ephemeral UI state lives here (what the user is dragging right now, where
// a placeholder should render). Authoritative rule data lives in TanStack Query
// (`useRuleQuery`). Components read these shallow fields and decide locally
// whether to render a hover/placeholder indicator.

export const QUERY_BUILDER_REDUCER_NAME = "query-builder";

const initialState = {
  // Currently-hovered drop target during a drag.
  hoverField: null, // { queryId, conditionGroupId, fieldId, side: "left" | "right" }

  // Condition placeholder drawn while a draggable entity-field hovers an empty group slot.
  placeholderTarget: null, // { queryId, conditionGroupId, type: "conditionGroups" | "joinGroups", isNestedGroup }

  // queryId of the Limit field currently focused/saving, if any.
  editingLimitQueryId: null,
};

const queryBuilderSlice = createSlice({
  name: QUERY_BUILDER_REDUCER_NAME,
  initialState,
  reducers: {
    setHoverField: (state, { payload }) => {
      state.hoverField = payload;
    },
    clearHoverField: (state) => {
      state.hoverField = null;
    },
    setPlaceholderTarget: (state, { payload }) => {
      state.placeholderTarget = payload;
    },
    clearPlaceholderTarget: (state) => {
      state.placeholderTarget = null;
    },
    setEditingLimit: (state, { payload }) => {
      state.editingLimitQueryId = payload;
    },
    clearEditingLimit: (state) => {
      state.editingLimitQueryId = null;
    },
    resetQueryBuilderUI: () => initialState,
  },
});

export const {
  setHoverField,
  clearHoverField,
  setPlaceholderTarget,
  clearPlaceholderTarget,
  setEditingLimit,
  clearEditingLimit,
  resetQueryBuilderUI,
} = queryBuilderSlice.actions;

export default queryBuilderSlice.reducer;

// ── Selectors ────────────────────────────────────────────────────────────────

export const useSelectHoverField = () =>
  useSelector((state) => state[QUERY_BUILDER_REDUCER_NAME].hoverField);

export const useSelectPlaceholderTarget = () =>
  useSelector((state) => state[QUERY_BUILDER_REDUCER_NAME].placeholderTarget);

export const useSelectIsFieldHovered = ({
  queryId,
  conditionGroupId,
  fieldId,
  side,
}) =>
  useSelector((state) => {
    const h = state[QUERY_BUILDER_REDUCER_NAME].hoverField;
    return (
      !!h &&
      h.queryId === queryId &&
      h.conditionGroupId === conditionGroupId &&
      h.fieldId === fieldId &&
      h.side === side
    );
  });

export const useSelectIsEditingLimit = (queryId) =>
  useSelector(
    (state) => state[QUERY_BUILDER_REDUCER_NAME].editingLimitQueryId === queryId,
  );

export const useSelectIsPlaceholderTarget = ({
  queryId,
  conditionGroupId,
  type,
}) =>
  useSelector((state) => {
    const p = state[QUERY_BUILDER_REDUCER_NAME].placeholderTarget;
    return (
      !!p &&
      p.queryId === queryId &&
      p.conditionGroupId === conditionGroupId &&
      p.type === type
    );
  });
